import React, { useEffect } from "react";
import styled from "styled-components";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { observer } from "mobx-react-lite";
import { Field } from "../../../shared/ui/Field";
import { Button } from "../../../shared/ui/Button";
import { Checkbox } from "../../../shared/ui/Checkbox";
import { TextMedium, TextSmall } from "../../../shared/ui/Typography";
import { colors } from "../../../shared/styles/colors";
import { userStore } from "../../../entities/user/model/user-store";
import { useNavigate } from "react-router-dom";
import { getApiUrl } from "../../../shared/api/config";
const FormWrapper = styled.form`
  display: flex;
  flex-direction: column;
  gap: 36px;
  width: 100%;
  max-width: 400px;
`;
const ErrorMessage = styled.div`
  color: ${colors.errorState};
  font-size: 12px;
  margin-top: -8px;
  margin-bottom: 4px;
`;
const RememberMeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
const ForgotPasswordLink = styled.a`
  color: ${colors.accentSecondary};
  text-decoration: none;
  font-size: 16px;

  &:hover {
    text-decoration: underline;
  }
`;
const SocialLoginContainer = styled.div`
  display: flex;
  gap: 32px;
  justify-content: center;
`;
const SocialButton = styled.button`
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${colors.accentQuaternary};
  }
`;
const RegisterRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 16px;
  gap: 8px;
`;
const RegisterLink = styled.a`
  color: ${colors.accentSecondary};
  text-decoration: none;
  font-size: 16px;
  &:hover {
    text-decoration: underline;
  }
`;
// Схема валідації для форми логіну
const loginSchema = z.object({
  email: z
    .string()
    .min(1, "Email обов'язковий")
    .email("Введіть коректний email"),
  password: z
    .string()
    .min(1, "Пароль обов'язковий")
    .min(6, "Пароль повинен містити мінімум 6 символів"),
  remember_me: z.boolean(),
});
// Тип даних форми на основі схеми
type LoginFormValues = z.infer<typeof loginSchema>;
export const LoginForm = observer(() => {
  const navigate = useNavigate();
  const methods = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
      remember_me: false,
    },
  });
  const { handleSubmit, watch, setValue, formState, setError } = methods;
  const remember_me = watch("remember_me");
  // Очищаємо помилки авторизації при розмонтуванні компонента
  useEffect(() => {
    return () => {
      userStore.resetAuthErrors();
    };
  }, []);
  // Обробляємо зміни статусу авторизації
  useEffect(() => {
    if (userStore.authStatus === "success" && userStore.isAuthenticated) {
      navigate("/");
    }
  }, [userStore.authStatus, userStore.isAuthenticated, navigate]);
  const onSubmit = handleSubmit(async (data: LoginFormValues) => {
    const success = await userStore.login({
      email: data.email,
      password: data.password,
      remember_me: data.remember_me,
    });
    if (!success && userStore.authError) {
      setError("root", {
        type: "manual",
        message: userStore.authError,
      });
      return;
    }
    navigate("/profile");
  });
  // const handleGoogleLogin = () => {
  //   window.location.href = `${getApiUrl()}/accounts/google/login/`;
  // };
  // LoginForm.tsx

  const handleGoogleLogin = () => {
    const redirectUri = encodeURIComponent(
      `${window.location.origin}/oauth-callback`
    );
    window.location.href = `${getApiUrl()}/accounts/google/login/?redirect_uri=${redirectUri}`;

  };
  return (
    <FormProvider {...methods}>
      <FormWrapper onSubmit={onSubmit} autoComplete="on">
        <Field
          name="email"
          type="email"
          autoComplete="email"
          onChange={(e) => setValue("email", e.target.value)}
          placeholder="E-mail *"
          error={methods.formState.errors.email?.message}
          isSuccessful={
            methods.formState.isValid && methods.formState.dirtyFields.email
          }
        />
        {methods.formState.errors.email && (
          <ErrorMessage>{methods.formState.errors.email.message}</ErrorMessage>
        )}
        <Field
          name="password"
          type="password"
          autoComplete={remember_me ? "current-password" : "new-password"}
          onChange={(e) => setValue("password", e.target.value)}
          placeholder="Пароль *"
          error={methods.formState.errors.password?.message}
          isSuccessful={
            methods.formState.isValid && methods.formState.dirtyFields.password
          }
        />
        {methods.formState.errors.password && (
          <ErrorMessage>
            {methods.formState.errors.password.message}
          </ErrorMessage>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <RememberMeRow>
            <Checkbox
              checked={remember_me}
              onChange={(checked) => setValue("remember_me", checked)}
            />
            <TextMedium>Запам'ятати мене</TextMedium>
          </RememberMeRow>
          <ForgotPasswordLink href="/reset-password/email">
            Забули пароль?
          </ForgotPasswordLink>
        </div>
        {formState.errors.root && (
          <ErrorMessage>{formState.errors.root.message}</ErrorMessage>
        )}
        <div className="flex-col">
          {userStore.authStatus === "pending" ? (
            <Button type="button" variant="primary" fullWidth disabled>
              Завантаження...
            </Button>
          ) : (
            <Button type="submit" variant="primary" fullWidth>
              Увійти до системи
            </Button>
          )}
          <RegisterRow>
            <TextMedium>Ви ще не зареєстровані?</TextMedium>
            <RegisterLink href="/register">Зареєструватись</RegisterLink>
          </RegisterRow>
        </div>
        <SocialLoginContainer>
          <SocialButton type="button" onClick={handleGoogleLogin}>
            <svg
              width="60"
              height="60"
              viewBox="0 0 60 60"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="60" height="60" rx="30" fill="#E9E9E9" />
              <g clipPath="url(#clip0_3636_362)">
                <path
                  d="M21.5 30.0002C21.5 28.4148 21.9368 26.9296 22.6957 25.6579V20.2637H17.3014C15.1605 23.0442 14 26.4329 14 30.0002C14 33.5676 15.1605 36.9563 17.3014 39.7368H22.6957V34.3425C21.9368 33.0709 21.5 31.5857 21.5 30.0002Z"
                  fill="#FBBD00"
                />
                <path
                  d="M30 38.5005L26.25 42.2505L30 46.0005C33.5674 46.0005 36.9561 44.84 39.7366 42.699V37.3105H34.3481C33.0652 38.0722 31.5738 38.5005 30 38.5005Z"
                  fill="#0F9D58"
                />
                <path
                  d="M22.695 34.3418L17.3008 39.736C17.7247 40.2865 18.1862 40.8137 18.6857 41.3132C21.7077 44.3352 25.7256 45.9995 29.9993 45.9995V38.4995C26.8978 38.4995 24.1795 36.8295 22.695 34.3418Z"
                  fill="#31AA52"
                />
                <path
                  d="M46 29.9993C46 29.0258 45.9119 28.0506 45.738 27.1007L45.5973 26.332H30V33.832H37.5907C36.8536 35.2983 35.7202 36.4947 34.348 37.3094L39.7365 42.6979C40.287 42.274 40.8142 41.8125 41.3137 41.313C44.3357 38.291 46 34.273 46 29.9993Z"
                  fill="#3C79E6"
                />
                <path
                  d="M36.0104 23.9896L36.6733 24.6524L41.9766 19.3492L41.3137 18.6863C38.2917 15.6643 34.2738 14 30 14L26.25 17.75L30 21.5C32.2704 21.5 34.4049 22.3841 36.0104 23.9896Z"
                  fill="#CF2D48"
                />
                <path
                  d="M29.9994 21.5V14C25.7257 14 21.7077 15.6643 18.6857 18.6863C18.1862 19.1857 17.7247 19.7129 17.3008 20.2634L22.695 25.6577C24.1796 23.17 26.8979 21.5 29.9994 21.5Z"
                  fill="#EB4132"
                />
              </g>
              <defs>
                <clipPath id="clip0_3636_362">
                  <rect
                    width="32"
                    height="32"
                    fill="white"
                    transform="translate(14 14)"
                  />
                </clipPath>
              </defs>
            </svg>
          </SocialButton>
        </SocialLoginContainer>
      </FormWrapper>
    </FormProvider>
  );
});
